import api from '../../axios';
import { handleApiError } from '../../errorHandler';
import {
  IFuelsIndicatorsMarket,
  ICreateFuelsIndicatorsMarketRequest,
  IUpdateFuelsIndicatorsMarketRequest,
  IPanelDashboardData,
  IPanelDashboardHistoryQuery,
  IPanelDashboardHistoryResponse,
  IDashboardChartMetadata,
} from './types';

const BASE_URL = '/fuels-indicators-market';

export const getPanelDashboard = async (): Promise<IPanelDashboardData> => {
  try {
    const response = await api.get<IPanelDashboardData>(`${BASE_URL}/panel-dashboard`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar painel de indicadores de mercado:', error);
    throw handleApiError(error);
  }
};

export const getDashboardChartMetadata = async (): Promise<IDashboardChartMetadata> => {
  try {
    const response = await api.get<IDashboardChartMetadata>(`${BASE_URL}/dashboard-chart-metadata`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar metadados dos gráficos do dashboard:', error);
    throw handleApiError(error);
  }
};

export const getPanelDashboardHistory = async (
  params: IPanelDashboardHistoryQuery
): Promise<IPanelDashboardHistoryResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('indicatorKey', params.indicatorKey);

    if (params.catalogId) queryParams.append('catalogId', params.catalogId);
    if (params.productLinesId) queryParams.append('productLinesId', params.productLinesId);
    if (params.ethanolType) queryParams.append('ethanolType', params.ethanolType);
    if (params.productLineId) queryParams.append('productLineId', params.productLineId);
    if (params.baseDate) queryParams.append('baseDate', params.baseDate);
    if (params.limit != null) queryParams.append('limit', String(params.limit));

    const response = await api.get<IPanelDashboardHistoryResponse>(
      `${BASE_URL}/panel-dashboard/history?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico do painel de indicadores:', error);
    throw handleApiError(error);
  }
};

export const getFuelsIndicatorsMarket = async (): Promise<IFuelsIndicatorsMarket[]> => {
  try {
    const response = await api.get(BASE_URL);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar indicadores de mercado:', error);
    throw handleApiError(error);
  }
};

export const getFuelsIndicatorsMarketById = async (id: string): Promise<IFuelsIndicatorsMarket> => {
  try {
    const response = await api.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const createFuelsIndicatorsMarket = async (
  data: ICreateFuelsIndicatorsMarketRequest
): Promise<IFuelsIndicatorsMarket> => {
  try {
    const response = await api.post(BASE_URL, data);
    return response.data;
  } catch (error: any) {
    console.error('Erro detalhado na criação do indicador de mercado:', error);
    if (error.response?.status === 500) {
      throw new Error(
        'Erro interno do servidor ao criar indicador. Por favor, verifique os dados e tente novamente.'
      );
    }
    throw handleApiError(error);
  }
};

export const updateFuelsIndicatorsMarket = async (
  id: string,
  data: IUpdateFuelsIndicatorsMarketRequest
): Promise<IFuelsIndicatorsMarket> => {
  try {
    const response = await api.patch(`${BASE_URL}/${id}`, data);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(
        `Erro ao atualizar indicador: ${error.response.data?.message || 'Erro de validação'}`
      );
    }
    throw handleApiError(error);
  }
};

export const deleteFuelsIndicatorsMarket = async (id: string): Promise<void> => {
  try {
    await api.delete(`${BASE_URL}/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

export type {
  IPanelDashboardData,
  IPanelDashboardMarketData,
  IPanelDashboardEthanolData,
  IPanelDashboardPetroleumPoleGroup,
  IPanelDashboardHistoryQuery,
  IPanelDashboardHistoryResponse,
  IPanelDashboardHistoryRecord,
} from './types';

export { PanelDashboardIndicatorKey } from './types';
